import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useApi } from '../hooks/useApi';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import ContactEmailThreads from '../components/ContactEmailThreads';
import MassEmailComposer from '../components/email/MassEmailComposer';
import OrganizationCombobox from '../components/OrganizationCombobox';
import EngagementsSection from '../components/EngagementsSection';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { formatDateTime, STAGE_DISPLAY_NAMES } from '../lib/utils';
import ContactTimeline from '../components/ContactTimeline';
import { 
  ArrowLeft, 
  Edit2, 
  Trash2, 
  User,
  Mail,
  Phone,
  Building2,
  Briefcase,
  Calendar,
  Clock,
  FileText,
  Save,
  X,
  Loader2,
  Plus,
  ExternalLink,
  MessageSquare,
  LayoutList,
  Send,
  Workflow,
  Sparkles,
  MapPin,
} from 'lucide-react';
import TrackVisitDialog from '../components/mileage/TrackVisitDialog';
import ContactVisitHistory from '../components/mileage/ContactVisitHistory';

const CONTACT_TYPE_TAGS = [
  "Decision Maker", "Influencer", "Champion", 
  "Finance", "Operations", "HR", "Other"
];

const CONSENT_STATUSES = [
  { id: 'unknown', label: 'Unknown', color: '#6B7280' },
  { id: 'opt_in', label: 'Opt-in', color: '#10B981' },
  { id: 'opt_out', label: 'Opt-out', color: '#EF4444' },
];

export default function ContactDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const { get, put, del, post, loading } = useApi();
  
  const [contact, setContact] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const timelineRef = useRef(null);
  const [emailComposerOpen, setEmailComposerOpen] = useState(false);
  const [editFocusField, setEditFocusField] = useState(null);
  const [emailHistory, setEmailHistory] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [createOppDialogOpen, setCreateOppDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  // Mileage / visit tracking
  const [trackVisitOpen, setTrackVisitOpen] = useState(false);
  const [visitsRefreshKey, setVisitsRefreshKey] = useState(0);
  
  const [editData, setEditData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    account_id: '',
    account_name: '',
    phone: '',
    title: '',
    contact_type_tags: [],
    consent_status: 'unknown',
    notes: '',
    next_follow_up_date: '',
  });

  const [oppFormData, setOppFormData] = useState({
    company_name: '',
    property_type: 'Hotel',
    deal_value: 0,
    description: '',
  });

  const fetchContact = async () => {
    try {
      const data = await get(`/contacts/${id}`);
      setContact(data);
      setEditData({
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        account_id: data.account_id || '',
        account_name: data.account_name || '',
        phone: data.phone || '',
        title: data.title || '',
        contact_type_tags: data.contact_type_tags || [],
        consent_status: data.consent_status || 'unknown',
        notes: data.notes || '',
        next_follow_up_date: data.next_follow_up_date || '',
      });
    } catch (error) {
      toast.error('Contact not found');
      navigate('/contacts');
    }
  };

  const fetchOpportunities = async () => {
    try {
      const data = await get(`/contacts/${id}/opportunities`);
      setOpportunities(data);
    } catch (error) {
      console.error('Failed to fetch opportunities:', error);
    }
  };

  const fetchEmailHistory = async () => {
    try {
      const data = await get(`/contacts/${id}/email-history`);
      setEmailHistory(data);
    } catch (error) {
      console.error('Failed to fetch email history:', error);
    }
  };

  const fetchAccounts = async () => {
    try {
      const data = await get('/accounts');
      setAccounts(data);
    } catch (error) {
      console.error('Failed to fetch accounts:', error);
    }
  };

  useEffect(() => {
    fetchContact();
    fetchOpportunities();
    fetchEmailHistory();
    fetchAccounts();
  }, [id]);

  const canEdit = () => {
    if (!contact) return false;
    if (isAdmin) return true;
    return contact.owner_id === user?.id;
  };

  const getConsentBadge = (status) => {
    const consent = CONSENT_STATUSES.find(c => c.id === status) || CONSENT_STATUSES[0];
    return (
      <div className="flex flex-col items-end gap-1" data-testid="consent-display">
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Email Consent</span>
        <button
          onClick={() => { setIsEditing(true); setEditFocusField('consent'); }}
          className="rounded-sm text-xs font-medium px-2.5 py-0.5 hover:opacity-90 transition"
          style={{ backgroundColor: consent.color, color: '#fff' }}
          title="Click to change"
          data-testid="consent-badge"
        >
          {consent.label}
        </button>
      </div>
    );
  };

  // ───── Quick Action handlers ─────
  const handleSendEmailAction = () => {
    if (!contact?.email) {
      toast.error('Add an email to this contact first');
      setIsEditing(true);
      setEditFocusField('email');
      return;
    }
    setEmailComposerOpen(true);
  };

  const handleQuickLogCall = () => {
    if (timelineRef.current?.openDialog) {
      timelineRef.current.openDialog('call');
    } else {
      toast.error('Activity timeline not ready');
    }
  };

  const handleQuickCreateOpportunity = () => {
    setOppFormData({
      company_name: contact?.account_name || '',
      property_type: 'Hotel',
      deal_value: 0,
      description: '',
    });
    setCreateOppDialogOpen(true);
  };

  const handleQuickStartWorkflow = () => {
    toast('Workflow Engine coming soon', {
      description: 'You\'ll be able to enroll contacts in nurture sequences and follow-up cadences here.',
    });
  };

  const lockedRecipientForComposer = contact ? {
    email: contact.email || '',
    name: `${contact.first_name || ''} ${contact.last_name || ''}`.trim(),
    company: contact.account_name || '',
    contact_title: contact.title || '',
    contact_id: contact.id,
  } : null;

  const toggleTag = (tag) => {
    setEditData(prev => ({
      ...prev,
      contact_type_tags: prev.contact_type_tags.includes(tag)
        ? prev.contact_type_tags.filter(t => t !== tag)
        : [...prev.contact_type_tags, tag]
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await put(`/contacts/${id}`, editData);
      toast.success('Contact updated successfully');
      setIsEditing(false);
      setEditFocusField(null);
      fetchContact();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await del(`/contacts/${id}`);
      toast.success('Contact deleted successfully');
      navigate('/contacts');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleLogContact = async () => {
    try {
      await post(`/contacts/${id}/log-contact`);
      toast.success('Contact interaction logged');
      fetchContact();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleCreateOpportunity = async (e) => {
    e.preventDefault();
    try {
      const oppData = {
        company_name: oppFormData.company_name || contact.account_name,
        contact_name: `${contact.first_name} ${contact.last_name}`,
        contact_email: contact.email,
        contact_phone: contact.phone || '',
        property_type: oppFormData.property_type,
        deal_value: oppFormData.deal_value,
        description: oppFormData.description,
        account_id: contact.account_id,
        primary_contact_id: contact.id,
        tags: ['from-contact'],
      };
      
      await post('/opportunities', oppData);
      toast.success('Opportunity created');
      setCreateOppDialogOpen(false);
      setOppFormData({ company_name: '', property_type: 'Hotel', deal_value: 0, description: '' });
      fetchOpportunities();
    } catch (error) {
      toast.error(error.message);
    }
  };

  if (!contact) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in max-w-4xl mx-auto" data-testid="contact-detail-page">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate('/contacts')}
          className="rounded-sm -ml-2"
          data-testid="back-to-contacts"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Contacts
        </Button>

        {/* Header Card */}
        <Card className="rounded-sm border">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div className="flex-1">
                {isEditing ? (
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <Input
                      value={editData.first_name}
                      onChange={(e) => setEditData(prev => ({ ...prev, first_name: e.target.value }))}
                      className="text-xl font-semibold rounded-sm"
                      placeholder="First Name"
                    />
                    <Input
                      value={editData.last_name}
                      onChange={(e) => setEditData(prev => ({ ...prev, last_name: e.target.value }))}
                      className="text-xl font-semibold rounded-sm"
                      placeholder="Last Name"
                    />
                  </div>
                ) : (
                  <h1 className="text-2xl font-semibold mb-2">
                    {contact.first_name} {contact.last_name}
                  </h1>
                )}
                
                <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                  {isEditing ? (
                    <Input
                      value={editData.title}
                      onChange={(e) => setEditData(prev => ({ ...prev, title: e.target.value }))}
                      className="w-48 rounded-sm"
                      placeholder="Title/Role"
                    />
                  ) : contact.title && (
                    <div className="flex items-center gap-1.5">
                      <Briefcase className="h-4 w-4" />
                      <span>{contact.title}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-1.5">
                    <Building2 className="h-4 w-4" />
                    <span>{contact.account_name}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <User className="h-4 w-4" />
                    <span>Owner: {contact.owner_name}</span>
                  </div>
                </div>
                
                {/* Last Activity - Prominent Display */}
                <div className="mt-3 pt-3 border-t border-border">
                  <div className="flex items-center gap-2" data-testid="last-activity-display">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Last Activity:</span>
                    <span className={`text-sm ${contact.last_activity_date ? 'text-foreground' : 'text-muted-foreground italic'}`}>
                      {contact.last_activity_date 
                        ? formatDateTime(contact.last_activity_date)
                        : 'No activity logged'}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                {isEditing ? (
                  <Select 
                    value={editData.consent_status} 
                    onValueChange={(v) => setEditData(prev => ({ ...prev, consent_status: v }))}
                  >
                    <SelectTrigger className="w-[120px] rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CONSENT_STATUSES.map((status) => (
                        <SelectItem key={status.id} value={status.id}>
                          {status.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  getConsentBadge(contact.consent_status)
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Action Bar — premium horizontal pill row */}
        {!isEditing && (
          <div
            className="flex items-stretch gap-2 overflow-x-auto -mx-1 px-1 sm:justify-start"
            data-testid="contact-quick-actions"
          >
            <button
              onClick={handleSendEmailAction}
              className="flex-1 sm:flex-none min-w-[110px] flex items-center justify-center gap-2 px-4 py-3 rounded-sm border bg-background hover:bg-[#B89B5E]/5 hover:border-[#B89B5E] transition group"
              data-testid="quick-action-send-email"
            >
              <Send className="h-4 w-4 text-[#B89B5E]" />
              <span className="text-sm font-medium">Send Email</span>
            </button>
            <button
              onClick={handleQuickLogCall}
              className="flex-1 sm:flex-none min-w-[110px] flex items-center justify-center gap-2 px-4 py-3 rounded-sm border bg-background hover:bg-[#B89B5E]/5 hover:border-[#B89B5E] transition"
              data-testid="quick-action-log-call"
            >
              <Phone className="h-4 w-4 text-[#B89B5E]" />
              <span className="text-sm font-medium">Log Call</span>
            </button>
            <button
              onClick={handleQuickCreateOpportunity}
              className="flex-1 sm:flex-none min-w-[140px] flex items-center justify-center gap-2 px-4 py-3 rounded-sm border bg-background hover:bg-[#B89B5E]/5 hover:border-[#B89B5E] transition"
              data-testid="quick-action-create-opp"
            >
              <Briefcase className="h-4 w-4 text-[#B89B5E]" />
              <span className="text-sm font-medium">Create Opp</span>
            </button>
            <button
              onClick={handleQuickStartWorkflow}
              className="flex-1 sm:flex-none min-w-[130px] flex items-center justify-center gap-2 px-4 py-3 rounded-sm border bg-background hover:bg-[#B89B5E]/5 hover:border-[#B89B5E] transition"
              data-testid="quick-action-workflow"
            >
              <Workflow className="h-4 w-4 text-[#B89B5E]" />
              <span className="text-sm font-medium">Workflow</span>
            </button>
          </div>
        )}

        {/* Contact Info & Details */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Contact Information */}
          <Card className="rounded-sm border">
            <CardHeader className="pb-4">
              <CardTitle className="text-base font-medium flex items-center gap-2">
                <User className="h-4 w-4" />
                Contact Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Email
                  </Label>
                  {!isEditing && contact.email && (
                    <button
                      onClick={handleSendEmailAction}
                      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm bg-[#B89B5E]/10 text-[#B89B5E] hover:bg-[#B89B5E]/20 transition"
                      data-testid="email-card-send-btn"
                    >
                      <Sparkles className="h-3 w-3" />
                      Send
                    </button>
                  )}
                </div>
                {isEditing ? (
                  <Input
                    type="email"
                    value={editData.email}
                    onChange={(e) => setEditData(prev => ({ ...prev, email: e.target.value }))}
                    className="rounded-sm"
                    autoFocus={editFocusField === 'email'}
                    data-testid="email-input"
                  />
                ) : contact.email ? (
                  <a 
                    href={`mailto:${contact.email}`}
                    className="text-sm text-primary hover:underline flex items-center gap-2"
                    data-testid="email-link"
                  >
                    <Mail className="h-3 w-3" />
                    {contact.email}
                  </a>
                ) : (
                  <p className="text-sm text-muted-foreground" data-testid="email-empty">
                    Not provided
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  Phone
                </Label>
                {isEditing ? (
                  <Input
                    type="tel"
                    value={editData.phone}
                    onChange={(e) => setEditData(prev => ({ ...prev, phone: e.target.value }))}
                    className="rounded-sm"
                    placeholder="Phone number"
                    autoFocus={editFocusField === 'phone'}
                    data-testid="phone-input"
                  />
                ) : contact.phone ? (
                  <a 
                    href={`tel:${contact.phone}`}
                    className="text-sm text-primary hover:underline flex items-center gap-2"
                    data-testid="phone-call-link"
                  >
                    <Phone className="h-3 w-3" />
                    {contact.phone}
                  </a>
                ) : (
                  <button
                    onClick={() => { setIsEditing(true); setEditFocusField('phone'); }}
                    className="text-sm text-[#B89B5E] hover:underline flex items-center gap-1 group"
                    data-testid="add-phone-btn-empty"
                  >
                    <Plus className="h-3 w-3 group-hover:scale-110 transition" /> Add phone
                  </button>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Organization
                </Label>
                {isEditing ? (
                  <OrganizationCombobox
                    value={editData.account_id}
                    valueName={editData.account_name}
                    onChange={(id, name) =>
                      setEditData(prev => ({ ...prev, account_id: id, account_name: name }))
                    }
                  />
                ) : contact.account_id ? (
                  <Link 
                    to={`/organizations/${contact.account_id}`}
                    className="text-sm text-primary hover:underline flex items-center gap-1"
                    data-testid="account-link"
                  >
                    {contact.account_name}
                    <ExternalLink className="h-3 w-3" />
                  </Link>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    {contact.account_name || 'Not linked'}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Contact Type Tags</Label>
                {isEditing ? (
                  <div className="flex flex-wrap gap-2">
                    {CONTACT_TYPE_TAGS.map((tag) => (
                      <Badge
                        key={tag}
                        variant={editData.contact_type_tags.includes(tag) ? "default" : "outline"}
                        className="cursor-pointer rounded-sm"
                        onClick={() => toggleTag(tag)}
                      >
                        {tag}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {contact.contact_type_tags?.length > 0 ? (
                      contact.contact_type_tags.map((tag) => (
                        <Badge key={tag} variant="outline" className="rounded-sm">
                          {tag}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-sm text-muted-foreground">No tags</span>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Dates & Notes */}
          <Card className="rounded-sm border">
            <CardHeader className="pb-4">
              <CardTitle className="text-base font-medium flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Activity & Notes
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Last Contacted</Label>
                  <p className="text-sm">
                    {contact.last_contacted_date 
                      ? formatDateTime(contact.last_contacted_date).split(',')[0] 
                      : 'Never'}
                  </p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Next Follow-Up</Label>
                  {isEditing ? (
                    <Input
                      type="date"
                      value={editData.next_follow_up_date}
                      onChange={(e) => setEditData(prev => ({ ...prev, next_follow_up_date: e.target.value }))}
                      className="rounded-sm h-8"
                    />
                  ) : (
                    <p className="text-sm">
                      {contact.next_follow_up_date 
                        ? formatDateTime(contact.next_follow_up_date).split(',')[0] 
                        : 'Not set'}
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Notes</Label>
                {isEditing ? (
                  <Textarea
                    value={editData.notes}
                    onChange={(e) => setEditData(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Internal notes..."
                    rows={4}
                    className="rounded-sm resize-none"
                  />
                ) : (
                  <p className="text-sm text-muted-foreground p-3 bg-muted/30 rounded-sm min-h-[80px]">
                    {contact.notes || 'No notes'}
                  </p>
                )}
              </div>

              {!isEditing && (
                <Button 
                  variant="outline" 
                  onClick={handleLogContact}
                  className="w-full rounded-sm"
                >
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Log Contact Interaction
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => setTrackVisitOpen(true)}
                className="w-full rounded-sm"
                data-testid="track-visit-btn"
              >
                <MapPin className="h-4 w-4 mr-2" />
                Track Visit
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Tabbed Content: Timeline, Opportunities, Emails, Visits */}
        <Tabs defaultValue="timeline" className="w-full">
          <TabsList className="grid w-full grid-cols-4 rounded-sm">
            <TabsTrigger value="timeline" className="rounded-sm" data-testid="timeline-tab">
              <MessageSquare className="h-4 w-4 mr-2" />
              Activity Timeline
            </TabsTrigger>
            <TabsTrigger value="opportunities" className="rounded-sm" data-testid="opportunities-tab">
              <Briefcase className="h-4 w-4 mr-2" />
              Opportunities ({opportunities.length})
            </TabsTrigger>
            <TabsTrigger value="emails" className="rounded-sm" data-testid="emails-tab">
              <Mail className="h-4 w-4 mr-2" />
              Emails ({emailHistory.length})
            </TabsTrigger>
            <TabsTrigger value="visits" className="rounded-sm" data-testid="visits-tab">
              <MapPin className="h-4 w-4 mr-2" />
              Visits
            </TabsTrigger>
          </TabsList>
          
          {/* Activity Timeline Tab */}
          <TabsContent value="timeline" className="mt-4">
            <ContactTimeline
              ref={timelineRef}
              contactId={id}
              opportunities={opportunities}
              onActivityLogged={fetchContact}
            />
          </TabsContent>
          
          {/* Opportunities Tab */}
          <TabsContent value="opportunities" className="mt-4">
            <Card className="rounded-sm border">
              <CardHeader className="pb-4 flex flex-row items-center justify-between">
                <CardTitle className="text-base font-medium flex items-center gap-2">
                  <Briefcase className="h-4 w-4" />
                  Linked Opportunities ({opportunities.length})
                </CardTitle>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    setOppFormData({ 
                      company_name: contact.account_name, 
                      property_type: 'Hotel', 
                      deal_value: 0, 
                      description: '' 
                    });
                    setCreateOppDialogOpen(true);
                  }}
                  className="rounded-sm"
                  data-testid="create-opp-detail-btn"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Create Opportunity
                </Button>
              </CardHeader>
              <CardContent>
                {opportunities.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-6">
                    No linked opportunities
                  </p>
                ) : (
                  <div className="space-y-2">
                    {opportunities.map((opp) => (
                      <Link
                        key={opp.id}
                        to={`/opportunity/${opp.id}`}
                        className="flex items-center justify-between p-3 bg-muted/30 rounded-sm hover:bg-muted/50 transition-colors"
                        data-testid={`opp-link-${opp.id}`}
                      >
                        <div>
                          <p className="font-medium text-sm">{opp.company_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {STAGE_DISPLAY_NAMES[opp.stage] || opp.stage}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium text-sm">${opp.deal_value.toLocaleString()}</p>
                          <ExternalLink className="h-3 w-3 text-muted-foreground" />
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Email History Tab */}
          <TabsContent value="emails" className="mt-4">
            <div className="space-y-6">
              {/* Gmail Threads - Live Integration */}
              <ContactEmailThreads 
                contactId={id}
                contactEmail={contact.email}
                contactName={`${contact.first_name || ''} ${contact.last_name || ''}`.trim()}
              />
              
              {/* CRM Email Sends - Legacy tracking */}
              {emailHistory.length > 0 && (
                <Card className="rounded-sm border">
                  <CardHeader className="pb-4">
                    <CardTitle className="text-base font-medium flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      CRM Email History ({emailHistory.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {emailHistory.map((email, idx) => (
                        <div
                          key={email.tracking_id || idx}
                          className="flex items-center justify-between p-3 bg-muted/30 rounded-sm"
                        >
                          <div>
                            <p className="font-medium text-sm">{email.subject}</p>
                            <p className="text-xs text-muted-foreground">
                              Sent {formatDateTime(email.sent_at)}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            {email.opened_at && (
                              <Badge variant="outline" className="text-xs rounded-sm">Opened</Badge>
                            )}
                            {email.clicked_at && (
                              <Badge variant="outline" className="text-xs rounded-sm">Clicked</Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Visits Tab */}
          <TabsContent value="visits" className="mt-4">
            <ContactVisitHistory
              contactId={id}
              contactName={`${contact.first_name || ''} ${contact.last_name || ''}`.trim()}
              refreshKey={visitsRefreshKey}
            />
          </TabsContent>
        </Tabs>

        {/* Engagements involving this contact */}
        <div className="mt-6">
          <EngagementsSection scope="contact" entityId={id} entityName={`${contact.first_name || ''} ${contact.last_name || ''}`.trim()} canEdit={user?.role !== 'executive_viewer'} />
        </div>

        {/* Track Visit dialog */}
        <TrackVisitDialog
          open={trackVisitOpen}
          onOpenChange={setTrackVisitOpen}
          contactId={id}
          contactName={`${contact.first_name || ''} ${contact.last_name || ''}`.trim()}
          onSaved={() => setVisitsRefreshKey((k) => k + 1)}
        />

        {/* Actions */}
        <div className="flex items-center justify-between pt-4 border-t">
          <Button 
            variant="outline" 
            onClick={() => navigate('/contacts')}
            className="rounded-sm"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Contacts
          </Button>
          
          <div className="flex items-center gap-2">
            {isEditing ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsEditing(false);
                    setEditData({
                      first_name: contact.first_name,
                      last_name: contact.last_name,
                      email: contact.email,
                      account_id: contact.account_id,
                      phone: contact.phone || '',
                      title: contact.title || '',
                      contact_type_tags: contact.contact_type_tags || [],
                      consent_status: contact.consent_status || 'unknown',
                      notes: contact.notes || '',
                      next_follow_up_date: contact.next_follow_up_date || '',
                    });
                  }}
                  className="rounded-sm"
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="rounded-sm"
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Save Changes
                </Button>
              </>
            ) : (
              <>
                {canEdit() && (
                  <Button
                    variant="outline"
                    onClick={() => setIsEditing(true)}
                    className="rounded-sm"
                  >
                    <Edit2 className="h-4 w-4 mr-2" />
                    Edit Contact
                  </Button>
                )}
                {canEdit() && (
                  <Button
                    variant="outline"
                    onClick={() => setDeleteDialogOpen(true)}
                    className="rounded-sm text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </Button>
                )}
              </>
            )}
          </div>
        </div>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Contact?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete {contact.first_name} {contact.last_name}. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                className="rounded-sm bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Create Opportunity Dialog */}
        <Dialog open={createOppDialogOpen} onOpenChange={setCreateOppDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold">Create Opportunity</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateOpportunity} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Company/Project Name</Label>
                <Input
                  value={oppFormData.company_name}
                  onChange={(e) => setOppFormData(prev => ({ ...prev, company_name: e.target.value }))}
                  placeholder={contact.account_name}
                  className="rounded-sm"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Property Type</Label>
                <Select
                  value={oppFormData.property_type}
                  onValueChange={(v) => setOppFormData(prev => ({ ...prev, property_type: v }))}
                >
                  <SelectTrigger className="rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Hotel">Hotel</SelectItem>
                    <SelectItem value="Resort">Resort</SelectItem>
                    <SelectItem value="Restaurant">Restaurant</SelectItem>
                    <SelectItem value="Spa">Spa</SelectItem>
                    <SelectItem value="Club">Club</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Deal Value ($)</Label>
                <Input
                  type="number"
                  value={oppFormData.deal_value}
                  onChange={(e) => setOppFormData(prev => ({ ...prev, deal_value: parseFloat(e.target.value) || 0 }))}
                  className="rounded-sm"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Description</Label>
                <Textarea
                  value={oppFormData.description}
                  onChange={(e) => setOppFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Brief description..."
                  rows={2}
                  className="rounded-sm resize-none"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setCreateOppDialogOpen(false)} className="rounded-sm">
                  Cancel
                </Button>
                <Button type="submit" className="rounded-sm" disabled={loading}>
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Mass Email Composer — pre-loaded with this contact as locked recipient */}
        <MassEmailComposer
          open={emailComposerOpen}
          onOpenChange={setEmailComposerOpen}
          lockedRecipient={lockedRecipientForComposer}
          lockedTitle={contact ? `Send Email · ${contact.first_name} ${contact.last_name}` : undefined}
          initialSubject="Quick note for {first_name}"
          initialBody="Hi {first_name},<br><br>"
        />
      </div>
    </Layout>
  );
}
